import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useEditorBundle, useBundleRoot } from '@/hooks/use-editor-bundle';
import { useCapabilityList } from '@/hooks/use-entity-list';
import { getWorkflowCapabilityFqcn } from '@/components/workflow-graph/utils/capability-fqcn';
import { useCapabilityUpdate } from '@/hooks/use-entity-mutations';
import { usePageTitle } from '@/hooks/use-page-title';
import { getCapabilityColors, getStatusStyle } from '@/lib/capability-colors';
import { useHeaderAction } from '@/lib/header-action-context';
import { useCapabilityDetail } from '@/lib/capability-detail-context';
import { useCapabilityActions } from '@/hooks/use-capability-actions';
import { api } from '@/lib/api-client';
import { logger } from '@/lib/telemetry';
import { WorkflowGraph } from '@/components/workflow-graph/WorkflowGraph';
import { CapabilitySettingsSheet } from '@/components/capability-detail/CapabilitySettingsSheet';
import { CapabilityHeaderActions } from '@/components/capability-detail/CapabilityHeaderActions';
import type { DeferredOverviewDirtyPayload } from '@/components/capability-detail/CapabilityOverviewTab';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { GitBranch, Lock, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import type { AslDefinition } from '@/types/workflow-graph';
import {
    EMPTY_ASL,
    extractDefinitionFromEntity,
} from '@/components/workflow-graph/utils/extract-workflow-definition';
import { CAPABILITY_TYPE_LABELS, capabilityDisplayTitle } from '@/types';
import type { CapabilityRevision } from '@/types';
import { WORKING_COPY_ID } from '@/components/capability-detail/CapabilityDetailLayout';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useSidebar } from '@/components/ui/sidebar';

interface CapabilityEntity {
    id: string;
    name: string;
    display_name?: string | null;
    app_key?: string;
    /** Canonical id app_key/name from API (workflow Task Resource suffix after "agent:" etc.) */
    fqcn?: string;
    slug?: string;
    description?: string;
    status?: string;
    working_revision?: {
        id?: string;
        version_string?: string;
        major?: number;
        minor?: number;
        patch?: number;
    } | null;
    immutable_revisions?: {
        id?: string;
        version_string?: string;
        major?: number;
        minor?: number;
        patch?: number;
    }[];
}

const LOG_PREFIX = 'workflow-detail-page';
const HEADER_LAYER_ID = 'workflow-page';

function mergeCapabilitiesByFqcn(
    localItems: CapabilityEntity[] | undefined,
    globalItems: CapabilityEntity[] | undefined,
    currentAppKey?: string,
): Array<{ name: string; fqcn: string; description?: string; app_key?: string }> {
    const merged = new Map<string, { name: string; fqcn: string; description?: string; app_key?: string }>();

    const addItems = (items: CapabilityEntity[] | undefined, fallbackAppKey?: string) => {
        for (const item of items ?? []) {
            const fqcn = getWorkflowCapabilityFqcn(item, fallbackAppKey);
            if (!merged.has(fqcn)) {
                merged.set(fqcn, {
                    name: item.name,
                    fqcn,
                    description: item.description,
                    app_key: item.app_key ?? fallbackAppKey,
                });
            }
        }
    };

    // Prefer app-scoped entries first, then fill missing entries from global scope.
    addItems(localItems, currentAppKey);
    addItems(globalItems);

    return Array.from(merged.values());
}

export default function WorkflowDetailPage() {
    const { appKey, name } = useParams();
    const queryClient = useQueryClient();
    const { data: bundle, isLoading, refetchWithoutCache } = useEditorBundle(appKey!, name!);
    const data = useBundleRoot(bundle);
    const { setHeaderTitle, pushHeaderLayer, removeHeaderLayer } = useHeaderAction();
    const { open: sidebarOpen, setOpen: setSidebarOpen, openMobile, setOpenMobile } = useSidebar();
    const capabilityDetail = useCapabilityDetail();
    const [isDirty, setIsDirty] = useState(false);
    const [currentDefinition, setCurrentDefinition] = useState<AslDefinition | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [overviewPatch, setOverviewPatch] = useState<Record<string, unknown>>({});
    const [selectedRevisionId, setSelectedRevisionId] = useState<string>(WORKING_COPY_ID);
    const updateMutation = useCapabilityUpdate(appKey ?? '', name ?? '');

    const { data: agentsData } = useCapabilityList(appKey, { type: 'agent', per_page: 200 });
    const { data: toolsData } = useCapabilityList(appKey, { type: 'tool', per_page: 200 });
    const { data: modelsData } = useCapabilityList(appKey, { type: 'model', per_page: 200 });
    const { data: policiesData } = useCapabilityList(appKey, { type: 'policy', per_page: 200 });
    const { data: workflowsData } = useCapabilityList(appKey, { type: 'workflow', per_page: 200 });
    const { data: globalAgentsData } = useCapabilityList(undefined, { type: 'agent', per_page: 200 });
    const { data: globalToolsData } = useCapabilityList(undefined, { type: 'tool', per_page: 200 });
    const { data: globalModelsData } = useCapabilityList(undefined, { type: 'model', per_page: 200 });
    const { data: globalPoliciesData } = useCapabilityList(undefined, { type: 'policy', per_page: 200 });
    const { data: globalWorkflowsData } = useCapabilityList(undefined, { type: 'workflow', per_page: 200 });

    const entity = (data ?? undefined) as unknown as CapabilityEntity | undefined;
    const workflowTitle = capabilityDisplayTitle({
        name: entity?.name ?? name ?? '',
        display_name: entity?.display_name,
    });

    usePageTitle(name ? `Workflow - ${workflowTitle}` : 'Workflow');

    useEffect(() => {
        const previousSidebarState = { open: sidebarOpen, openMobile };
        setSidebarOpen(false);
        setOpenMobile(false);

        return () => {
            setSidebarOpen(previousSidebarState.open);
            setOpenMobile(previousSidebarState.openMobile);
        };
    }, []);

    useEffect(() => {
        if (!data || !name) return;
        const colors = getCapabilityColors('workflow');
        const WfIcon = colors.icon;
        const statusStyle = getStatusStyle(entity?.status ?? 'active');
        const archived = (entity?.status ?? '').toLowerCase() === 'archived';
        const badges = (
            <div className="flex flex-wrap items-center gap-1.5 shrink-0">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors.bgLight} ${colors.text}`}>
                    {CAPABILITY_TYPE_LABELS.workflow ?? 'workflow'}
                </span>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusStyle}`}>
                    {entity?.status ?? 'active'}
                </span>
                <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600">
                    {(entity as { visibility?: string } | undefined)?.visibility || 'account'}
                </span>
                {archived && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                        <Lock size={11} /> Archived
                    </span>
                )}
            </div>
        );
        setHeaderTitle({
            icon: (
                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${colors.bgLight}`}>
                    <WfIcon size={16} className={colors.text} />
                </div>
            ),
            title: workflowTitle,
            subtitle: entity?.fqcn ?? (appKey ? `${appKey}/${name}` : name),
            badges,
        });
        return () => setHeaderTitle(null);
    }, [data, name, appKey, workflowTitle, entity?.fqcn, entity?.status, setHeaderTitle]);

    const definition = useMemo<AslDefinition>(
        () => (data ? extractDefinitionFromEntity(data as Record<string, unknown>, selectedRevisionId, WORKING_COPY_ID) : EMPTY_ASL),
        [data, selectedRevisionId],
    );

    const workflowId = entity?.id ?? '';
    const workflowName = workflowTitle;
    const workflowKey = entity
        ? getWorkflowCapabilityFqcn(entity, appKey)
        : (appKey && name ? `${appKey}/${name}` : (name ?? ''));
    const isArchived = (entity?.status ?? '').toLowerCase() === 'archived';

    const hasWorkingRev = entity?.working_revision != null;
    const immutableRevisions = useMemo<CapabilityRevision[]>(
        () => (entity?.immutable_revisions ?? []) as CapabilityRevision[],
        [entity?.immutable_revisions],
    );
    const hasVersionSelector = hasWorkingRev || immutableRevisions.length > 0;
    const isViewingPublished = selectedRevisionId !== WORKING_COPY_ID;

    useEffect(() => {
        capabilityDetail?.setIsViewingPublished(isViewingPublished);
    }, [isViewingPublished, capabilityDetail]);

    useEffect(() => {
        capabilityDetail?.setSelectedRevisionId(selectedRevisionId);
    }, [selectedRevisionId, capabilityDetail]);

    useEffect(() => {
        if (!data) return;
        if (!hasWorkingRev && selectedRevisionId === WORKING_COPY_ID && immutableRevisions.length > 0) {
            setSelectedRevisionId(immutableRevisions[0].id);
        }
    }, [data, hasWorkingRev, selectedRevisionId, immutableRevisions]);

    const prevWCVRef = useRef(capabilityDetail?.workingCopyVersion ?? 0);
    useEffect(() => {
        const wcv = capabilityDetail?.workingCopyVersion ?? 0;
        if (wcv > prevWCVRef.current) {
            setSelectedRevisionId(WORKING_COPY_ID);
        }
        prevWCVRef.current = wcv;
    }, [capabilityDetail?.workingCopyVersion]);

    const handleGraphChange = useCallback((dirty: boolean, def: AslDefinition | null) => {
        setIsDirty(dirty);
        setCurrentDefinition(def);
    }, []);

    const handleOverviewDeferredDirtyChange = useCallback((payload: DeferredOverviewDirtyPayload) => {
        setOverviewPatch((prev) => {
            const next = payload.patch;
            if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
            return next;
        });
    }, []);

    const overviewDirty = Object.keys(overviewPatch).length > 0;

    const overviewPatchRef = useRef(overviewPatch);
    overviewPatchRef.current = overviewPatch;

    const handleSave = useCallback(async () => {
        if (!appKey || !name) return;
        const graphDirty = isDirty && currentDefinition;
        const ovPatch = overviewPatchRef.current;
        const ovDirty = Object.keys(ovPatch).length > 0;
        if (!graphDirty && !ovDirty) return;
        setIsSaving(true);
        try {
            if (graphDirty) {
                await api.post(
                    `/applications/${appKey}/capabilities/${encodeURIComponent(name)}/upsert`,
                    {
                        targetRevision: 'working',
                        type_config: {
                            implementation: {
                                kind: 'workflow-asl',
                                entrypoint: 'asl.json',
                                execution: { kind: 'workflow-engine' },
                            },
                        },
                        files: [
                            { path: 'asl.json', content: JSON.stringify(currentDefinition), contentType: 'application/json' },
                        ],
                    },
                );
                setIsDirty(false);
            }
            if (ovDirty) {
                await updateMutation.mutateAsync(ovPatch);
                setOverviewPatch({});
            }
            await refetchWithoutCache();
            queryClient.invalidateQueries({ queryKey: ['capability', appKey, name] });
            toast.success('Workflow saved', { description: 'Workflow definition saved to database.' });
            logger.info(`${LOG_PREFIX} handleSave: workflow saved`, { name });
        } catch (err) {
            logger.error(`${LOG_PREFIX} handleSave: failed`, { error: err });
            toast.error('Failed to save workflow', {
                description: 'An unexpected error occurred. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    }, [appKey, name, currentDefinition, isDirty, queryClient, refetchWithoutCache, updateMutation]);

    const anyDirty = isDirty || overviewDirty;

    const actions = useCapabilityActions({
        appKey: appKey!,
        name: name!,
        typeLabel: 'Workflow',
        data: data as Record<string, unknown> | null,
        refetch: refetchWithoutCache,
        customSave: handleSave,
        customIsDirty: anyDirty,
        customIsSaving: isSaving,
    });

    const saveRef = useRef(actions.save);
    saveRef.current = actions.save;
    const publishRef = useRef(actions.publish);
    publishRef.current = actions.publish;
    const editRef = useRef(actions.edit);
    editRef.current = actions.edit;

    useEffect(() => {
        if (!data) return;
        if (isArchived) {
            removeHeaderLayer(HEADER_LAYER_ID);
            return;
        }
        pushHeaderLayer(HEADER_LAYER_ID, {
            action: (
                <CapabilityHeaderActions
                    typeLabel="Workflow"
                    hasWorkingRevision={actions.hasWorkingRevision}
                    isViewingPublished={actions.isViewingPublished}
                    isArchived={isArchived}
                    selectedPublishedLabel={actions.selectedPublishedLabel}
                    isDirty={actions.isDirty}
                    isSaving={actions.isSaving}
                    isPublishing={actions.isPublishing}
                    onSave={() => void saveRef.current()}
                    onPublish={() => void publishRef.current()}
                    onEdit={() => editRef.current()}
                    onArchive={() => actions.openArchiveDialog()}
                    onSettings={() => actions.openSettings()}
                />
            ),
        });
        return () => removeHeaderLayer(HEADER_LAYER_ID);
    }, [data, isArchived, actions.hasWorkingRevision, actions.isViewingPublished, actions.selectedPublishedLabel, actions.isDirty, actions.isSaving, actions.isPublishing, pushHeaderLayer, removeHeaderLayer]);

    useEffect(() => {
        if (isLoading || !data || !hasVersionSelector) {
            pushHeaderLayer(HEADER_LAYER_ID, { versionSelector: null });
            return;
        }
        const baseRevId = (entity?.working_revision as { base_revision_id?: string } | null)?.base_revision_id;
        const baseRev = baseRevId ? immutableRevisions.find((r) => r.id === baseRevId) : null;
        const versionSelectorNode = (
            <div className="flex items-center gap-2">
                <Select value={selectedRevisionId} onValueChange={setSelectedRevisionId}>
                    <SelectTrigger className="h-7 w-auto max-w-[280px] text-xs gap-1">
                        <GitBranch size={11} className="shrink-0" />
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {hasWorkingRev && (
                            <SelectItem value={WORKING_COPY_ID}>
                                Working Copy
                                {baseRev ? ` (from v${baseRev.version_string})` : null}
                            </SelectItem>
                        )}
                        {immutableRevisions.map((rev) => (
                            <SelectItem key={rev.id} value={rev.id}>
                                v{rev.version_string}
                                {rev.status === 'deprecated' ? ' (deprecated)' : ''}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {isViewingPublished && (
                    <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-600">
                        Read-only
                    </span>
                )}
            </div>
        );
        pushHeaderLayer(HEADER_LAYER_ID, { versionSelector: versionSelectorNode });
    }, [isLoading, data, hasVersionSelector, hasWorkingRev, selectedRevisionId, isViewingPublished, immutableRevisions, entity?.working_revision, pushHeaderLayer]);

    const agents = useMemo(
        () => mergeCapabilitiesByFqcn(agentsData?.data, globalAgentsData?.data, appKey),
        [agentsData, globalAgentsData, appKey],
    );
    const tools = useMemo(
        () => mergeCapabilitiesByFqcn(toolsData?.data, globalToolsData?.data, appKey),
        [toolsData, globalToolsData, appKey],
    );
    const models = useMemo(
        () => mergeCapabilitiesByFqcn(modelsData?.data, globalModelsData?.data, appKey),
        [modelsData, globalModelsData, appKey],
    );
    const policies = useMemo(
        () => mergeCapabilitiesByFqcn(policiesData?.data, globalPoliciesData?.data, appKey),
        [policiesData, globalPoliciesData, appKey],
    );
    const workflows = useMemo(
        () => mergeCapabilitiesByFqcn(workflowsData?.data, globalWorkflowsData?.data, appKey),
        [workflowsData, globalWorkflowsData, appKey],
    );
    const availableWorkflows = useMemo(
        () => workflows
            .filter((w) => w.fqcn !== `${appKey}/${name}`)
            .map((w) => ({
                id: w.fqcn,
                key: w.fqcn,
                name: w.name,
                fqcn: w.fqcn,
                app_key: w.app_key,
                definition: EMPTY_ASL,
            })),
        [workflows, appKey, name],
    );

    if (isLoading) return <div className="p-6">Loading workflow graph...</div>;
    if (!data) return <div className="p-6">Workflow not found</div>;

    const isSettingsOpen = capabilityDetail?.isSettingsOpen ?? false;

    return (
        <>
            <div className="flex h-full min-h-0 flex-1">
                <WorkflowGraph
                    transparentCanvas
                    definition={definition}
                    workflowId={workflowId}
                    workflowKey={workflowKey}
                    workflowName={workflowName}
                    appSlug={appKey}
                    readOnly={!actions.hasWorkingRevision || isViewingPublished}
                    onGraphChange={handleGraphChange}
                    agents={agents}
                    tools={tools}
                    models={models}
                    policies={policies}
                    availableWorkflows={availableWorkflows}
                />
                <CapabilitySettingsSheet
                    isOpen={isSettingsOpen}
                    onClose={() => capabilityDetail?.setIsSettingsOpen(false)}
                    data={(data ?? {}) as Record<string, unknown>}
                    typeLabel="Workflow"
                    overviewPersistMode="deferred"
                    onOverviewDeferredDirtyChange={handleOverviewDeferredDirtyChange}
                    pendingOverviewPatch={overviewPatch}
                    onSaveDeferred={() => void handleSave()}
                    isOverviewDirty={overviewDirty || isDirty}
                    isSaving={isSaving}
                    overviewFormDisabled={!actions.hasWorkingRevision || isViewingPublished}
                    hasWorkingRevision={actions.hasWorkingRevision}
                />
            </div>
            <AlertDialog open={actions.replaceDialogOpen} onOpenChange={actions.setReplaceDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Pencil size={18} className="text-amber-600" />
                            Replace working copy?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            A working copy already exists. Replacing it will discard any unsaved changes
                            and create a new working copy based on the selected published version.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={actions.handleConfirmReplace}>
                            Replace
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
